<template>
  <div class="ai-agent-container">
    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 顶部按钮组 -->
      <div class="button-group">
        <button v-for="button in mainButtons" :key="button.id" class="main-button"
          :class="{ active: activeMainButton === button.id }" @click="handleMainButtonClick(button.id)">
          {{ button.label }}
        </button>
      </div>

      <!-- 内容区域 -->
      <div class="content-area">
        <!-- 接口配置内容 -->
        <div v-if="activeMainButton === 'apiConfig'" class="config-content">
          <div class="config-header">
            <h3 class="config-title">大模型接口配置</h3>
            <p class="config-description">配置各种大模型接口参数，用于AI智能体的核心能力</p>
          </div>

          <div class="model-cards">
            <div v-for="model in modelConfigs" :key="model.id" class="model-card"
              :class="{ active: activeModel === model.id }" @click="handleModelSelect(model.id)">
              <div class="model-icon">
                <el-icon size="24">
                  <Cpu />
                </el-icon>
              </div>
              <div class="model-info">
                <h4 class="model-name">{{ model.name }}</h4>
                <p class="model-desc">{{ model.description }}</p>
              </div>
              <div class="model-status">
                <el-tag :type="model.status ? 'success' : 'danger'" size="small">
                  {{ model.status ? '已配置' : '未配置' }}
                </el-tag>
              </div>
            </div>
          </div>

          <!-- 模型配置表单 -->
          <div class="config-form">
            <el-form :model="apiForm" :rules="apiRules" ref="apiFormRef" label-width="120px">
              <el-form-item label="模型名称" prop="name">
                <el-input v-model="apiForm.name" placeholder="请输入模型名称" disabled />
              </el-form-item>

              <el-form-item label="接口地址" prop="openai_url">
                <el-input v-model="apiForm.openai_url" placeholder="请输入接口地址" />
              </el-form-item>

              <el-form-item label="模型标识" prop="openai_model">
                <el-input v-model="apiForm.openai_model" placeholder="请输入模型标识" />
              </el-form-item>

              <el-form-item label="API密钥" prop="openai_key">
                <el-input v-model="apiForm.openai_key" type="password" placeholder="请输入API密钥" show-password />
              </el-form-item>

              <el-form-item label="最大令牌数" prop="openai_max_tokens">
                <el-input-number v-model="apiForm.openai_max_tokens" :min="100" :max="10000" />
                <span class="unit-text">tokens</span>
              </el-form-item>

              <el-form-item label="温度参数" prop="openai_temperature">
                <el-slider v-model="apiForm.openai_temperature" :min="0" :max="2" :step="0.1" show-stops />
                <span class="slider-value">{{ apiForm.openai_temperature }}</span>
              </el-form-item>

              <el-form-item label="启用状态" prop="openai_enabled">
                <el-switch v-model="apiForm.openai_enabled" :active-value="1" :inactive-value="0" />
              </el-form-item>

              <!-- 在API配置表单的按钮组部分修改 -->
              <el-form-item>
                <el-button type="primary" @click="handleApiSave" :loading="saving">
                  保存配置
                </el-button>
                <el-button @click="handleApiTest" :loading="testingApiConnection">测试连接</el-button>
                <el-button @click="handleApiReset">重置</el-button>
              </el-form-item>

              <!-- 在按钮组下面添加测试结果显示 -->
              <div v-if="apiTestResult !== null" class="test-result">
                <el-alert :title="apiTestResult ? '模型连接测试成功' : '模型连接测试失败'" :type="apiTestResult ? 'success' : 'error'"
                  :description="apiTestMessage" show-icon :closable="false" />
              </div>
            </el-form>
          </div>
        </div>

        <!-- 智能体配置内容 -->
        <div v-if="activeMainButton === 'agentConfig'" class="config-content">
          <div class="config-header">
            <h3 class="config-title">Coze智能体配置</h3>
            <p class="config-description">配置Coze平台智能体参数，实现个性化AI助手</p>
          </div>

          <div class="agent-form">
            <el-form :model="agentForm" :rules="agentRules" ref="agentFormRef" label-width="120px">
              <el-form-item label="智能体名称" prop="name">
                <el-input v-model="agentForm.name" placeholder="请输入智能体名称" />
              </el-form-item>

              <el-form-item label="智能体密钥" prop="coze_access_token">
                <el-input v-model="agentForm.coze_access_token" type="password" placeholder="请输入智能体密钥" show-password />
              </el-form-item>

              <el-form-item label="智能体ID" prop="coze_botid">
                <el-input v-model="agentForm.coze_botid" placeholder="请输入智能体ID" />
              </el-form-item>

              <el-form-item label="接口地址" prop="apiUrl">
                <el-input v-model="agentForm.apiUrl" placeholder="请输入接口地址" />
                <template #append>
                  <el-button @click="setDefaultCozeUrl">默认地址</el-button>
                </template>
              </el-form-item>

              <el-form-item label="对话模式" prop="coze_chat_mode">
                <el-radio-group v-model="agentForm.coze_chat_mode">
                  <el-radio label="stream">流式传输</el-radio>
                  <el-radio label="batch">批量处理</el-radio>
                </el-radio-group>
              </el-form-item>

              <el-form-item label="响应超时" prop="coze_timeout">
                <el-input-number v-model="agentForm.coze_timeout" :min="10" :max="120" />
                <span class="unit-text">秒</span>
              </el-form-item>

              <el-form-item label="启用状态" prop="coze_enabled">
                <el-switch v-model="agentForm.coze_enabled" :active-value="1" :inactive-value="0" />
              </el-form-item>

              <!-- 在智能体配置表单的按钮组后面添加 -->
              <el-form-item>
                <el-button type="primary" @click="handleAgentSave" :loading="saving">
                  保存配置
                </el-button>
                <el-button @click="handleAgentTest" :loading="saving">测试连接</el-button>
                <el-button @click="handleAgentReset">重置</el-button>
              </el-form-item>

              <!-- 添加测试结果显示 -->
              <div v-if="agentTestResult !== null" class="test-result">
                <el-alert :title="agentTestResult ? '智能体连接测试成功' : '智能体连接测试失败'"
                  :type="agentTestResult ? 'success' : 'error'" :description="agentTestMessage" show-icon
                  :closable="false" />
              </div>
            </el-form>
          </div>
        </div>

        <!-- 存储配置内容 -->
        <div v-if="activeMainButton === 'storageConfig'" class="config-content">
          <div class="config-header">
            <h3 class="config-title">云存储配置</h3>
            <p class="config-description">配置云存储服务，用于文件存储和数据备份</p>

            <!-- 当前使用的云服务商显示 -->
            <div class="current-provider-info">
              <el-tag v-if="currentCloudProvider === 'local'" type="info" size="large">
                <el-icon>
                  <InfoFilled />
                </el-icon>
                当前使用：本地存储（由总后台配置）
              </el-tag>
              <el-tag v-else :type="getProviderTagType(currentCloudProvider)" size="large">
                <el-icon>
                  <SuccessFilled />
                </el-icon>
                当前使用：{{ getProviderName(currentCloudProvider) }}
              </el-tag>
            </div>
          </div>

          <div class="storage-cards">
            <div v-for="storage in storageConfigs" :key="storage.id" class="storage-card"
              :class="{ active: activeStorage === storage.id }" @click="handleStorageSelect(storage.id)">
              <div class="storage-icon">
                <el-icon size="24">
                  <Cloudy />
                </el-icon>
              </div>
              <div class="storage-info">
                <h4 class="storage-name">{{ storage.name }}</h4>
                <p class="storage-desc">{{ storage.description }}</p>
              </div>
              <div class="storage-status">
                <el-tag :type="storage.status ? 'success' : 'danger'" size="small">
                  {{ storage.status ? '已配置' : '未配置' }}
                </el-tag>
              </div>
            </div>
          </div>

          <!-- 存储配置表单 -->
          <div class="config-form">
            <el-form :model="storageForm" :rules="storageRules" ref="storageFormRef" label-width="120px">
              <el-form-item label="服务商" prop="provider">
                <el-input v-model="storageForm.provider" placeholder="请输入云存储服务商" disabled />
              </el-form-item>

              <el-form-item :label="storageForm.provider === '阿里云OSS' ? 'AccessKey' : 'AccessKey'" prop="accessKey">
                <el-input v-model="storageForm.accessKey"
                  :placeholder="storageForm.provider === '阿里云OSS' ? '请输入AccessKey ID' : '请输入AccessKey'" />
              </el-form-item>

              <el-form-item :label="storageForm.provider === '阿里云OSS' ? 'SecretKey' : 'SecretKey'" prop="secretKey">
                <el-input v-model="storageForm.secretKey" type="password"
                  :placeholder="storageForm.provider === '阿里云OSS' ? '请输入AccessKey Secret' : '请输入SecretKey'"
                  show-password />
              </el-form-item>

              <el-form-item label="存储空间" prop="bucket">
                <el-input v-model="storageForm.bucket" placeholder="请输入Bucket名称" />
              </el-form-item>

              <el-form-item label="访问域名" prop="domain">
                <el-input v-model="storageForm.domain" placeholder="请输入访问域名" />
              </el-form-item>

              <el-form-item :label="storageForm.provider === '阿里云OSS' ? '存储区域' : '存储区域'" prop="region">
                <el-input v-model="storageForm.region"
                  :placeholder="storageForm.provider === '阿里云OSS' ? '例如：oss-cn-hangzhou.aliyuncs.com' : '请输入存储区域'" />
              </el-form-item>

              <el-form-item label="启用状态" prop="enabled">
                <el-switch v-model="storageForm.enabled" :active-value="1" :inactive-value="0" />
              </el-form-item>

              <el-form-item>
                <el-button type="primary" @click="handleStorageSave" :loading="saving">
                  保存配置
                </el-button>
                <el-button @click="handleStorageTest" :loading="testingStorage">测试连接</el-button>
                <el-button @click="handleStorageReset">重置</el-button>
              </el-form-item>

              <!-- 测试结果显示 -->
              <div v-if="storageTestResult !== null" class="test-result">
                <el-alert :title="storageTestResult ? '连接测试成功' : '连接测试失败'"
                  :type="storageTestResult ? 'success' : 'error'" :description="storageTestMessage" show-icon
                  :closable="false" />
              </div>
            </el-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { ElMessage } from 'element-plus'
import { Cpu, Cloudy, InfoFilled, SuccessFilled } from '@element-plus/icons-vue'

export default {
  name: 'AiAgent',
  components: {
    Cpu,
    Cloudy,
    InfoFilled,
    SuccessFilled
  },
  data() {
    return {
      // 响应式数据
      activeMainButton: 'apiConfig',
      activeModel: 'zhipu',
      activeStorage: 'aliyun',
      saving: false,
      testingStorage: false,
      apiFormRef: null,
      agentFormRef: null,
      storageFormRef: null,
      // 智能体测试结果
      agentTestResult: null,
      agentTestMessage: '',
      // 存储所有配置数据
      allApiConfigs: {},
      allStorageConfigs: {},
      currentCloudProvider: 'aliyun',
      // 大模型测试相关
      testingApiConnection: false,
      apiTestResult: null,
      apiTestMessage: '',
      // 存储测试结果
      storageTestResult: null,
      storageTestMessage: '',

      // 主按钮配置
      mainButtons: [
        { id: 'apiConfig', label: '接口配置' },
        { id: 'agentConfig', label: '智能体配置' },
        { id: 'storageConfig', label: '存储配置' }
      ],

      // 模型配置数据
      modelConfigs: [
        {
          id: 'zhipu',
          name: '智谱大模型',
          description: 'GLM系列模型，支持多种自然语言任务',
          status: false,
          preset: {
            openai_url: 'https://open.bigmodel.cn/api/paas/v4/chat/completions',
            openai_model: 'glm-4-flash'
          }
        },
        {
          id: 'aliyun',
          name: '阿里通义千问',
          description: '通义千问系列模型，强大的中文理解能力',
          status: false,
          preset: {
            openai_url: 'https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions',
            openai_model: 'qwen-max'
          }
        },
        {
          id: 'baidu',
          name: '百度千帆大模型',
          description: '文心一言系列模型，百度生态深度整合',
          status: false,
          preset: {
            openai_url: 'https://qianfan.baidubce.com/v2/chat/completions',
            openai_model: 'ernie-4.0-8k'
          }
        },
        {
          id: 'tencent',
          name: '腾讯混元大模型',
          description: '混元系列模型，腾讯生态支持',
          status: false,
          preset: {
            openai_url: 'https://api.hunyuan.cloud.tencent.com/v1/chat/completions',
            openai_model: 'hunyuan-turbo-latest'
          }
        },
        {
          id: 'deepseek',
          name: 'DeepSeek深度求索',
          description: '深度求索模型，性价比优秀',
          status: false,
          preset: {
            openai_url: 'https://api.deepseek.com/chat/completions',
            openai_model: 'deepseek-chat'
          }
        },
        {
          id: 'custom',
          name: '自定义大模型',
          description: '自定义接口配置，支持任意兼容API',
          status: false,
          preset: {
            openai_url: 'https://api.deepseek1.com/chat/completions',
            openai_model: ''
          }
        }
      ],

      // 存储配置数据
      storageConfigs: [
        {
          id: 'aliyun',
          name: '阿里云OSS',
          description: '阿里云对象存储服务，稳定可靠',
          status: false
        },
        {
          id: 'tencent',
          name: '腾讯云COS',
          description: '腾讯云对象存储，腾讯生态整合',
          status: false
        },
        {
          id: 'qiniu',
          name: '七牛云Kodo',
          description: '七牛云存储，CDN加速优秀',
          status: false
        }
      ],

      // API配置表单
      apiForm: {
        name: '智谱大模型',
        openai_url: '',
        openai_model: '',
        openai_key: '',
        openai_max_tokens: 2000,
        openai_temperature: 0.7,
        openai_enabled: 1
      },

      // 智能体配置表单
      agentForm: {
        name: '',
        coze_access_token: '',
        coze_botid: '',
        apiUrl: 'https://api.coze.cn/v3/chat',
        coze_chat_mode: 'stream',
        coze_timeout: 30,
        coze_enabled: 1
      },

      // 存储配置表单
      storageForm: {
        provider: '阿里云OSS',
        accessKey: '',
        secretKey: '',
        bucket: '',
        domain: '',
        region: '',
        enabled: 1
      },

      // API配置验证规则
      apiRules: {
        openai_url: [
          { required: true, message: '请输入接口地址', trigger: 'blur' }
        ],
        openai_model: [
          { required: true, message: '请输入模型标识', trigger: 'blur' }
        ],
        openai_key: [
          { required: true, message: '请输入API密钥', trigger: 'blur' }
        ]
      },

      // 智能体配置验证规则
      agentRules: {
        name: [
          { required: true, message: '请输入智能体名称', trigger: 'blur' }
        ],
        coze_access_token: [
          { required: true, message: '请输入智能体密钥', trigger: 'blur' }
        ],
        coze_botid: [
          { required: true, message: '请输入智能体ID', trigger: 'blur' }
        ]
      },

      // 存储配置验证规则
      storageRules: {
        accessKey: [
          { required: true, message: '请输入访问密钥', trigger: 'blur' }
        ],
        secretKey: [
          { required: true, message: '请输入秘密密钥', trigger: 'blur' }
        ],
        bucket: [
          { required: true, message: '请输入存储空间', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('store_user_info') || '{}')
    },
    userId() {
      return this.userInfo.id
    },
    storeId() {
      return this.userInfo.store_id
    }
  },
  methods: {
    handleMainButtonClick(buttonId) {
      this.activeMainButton = buttonId
      // 切换时加载对应配置
      if (buttonId === 'apiConfig') {
        this.fetchAllApiConfigs()
      } else if (buttonId === 'agentConfig') {
        this.fetchAgentConfig()
      } else if (buttonId === 'storageConfig') {
        this.fetchAllStorageConfigs()
        this.fetchCloudProvider()
      }
    },

    // 获取云服务商配置
    async fetchCloudProvider() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/store/api/LaBasicSettingsController.php`, {
          action: 'get_cloud_provider',
          store_id: this.storeId,
          user_id: this.userId
        })
        if (data.data) {
          this.currentCloudProvider = data.data.cloud_provider || 'aliyun'
        }
      } catch (error) {
        console.error('获取云服务商配置失败:', error)
      }
    },

    // 更新云服务商配置
    async updateCloudProvider(provider) {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/store/api/LaBasicSettingsController.php`, {
          action: 'update_cloud_provider',
          store_id: this.storeId,
          user_id: this.userId,
          cloud_provider: provider
        })

        if (data.success) {
          this.currentCloudProvider = provider
          ElMessage.success(`已切换到${this.getProviderName(provider)}`)
        } else {
          ElMessage.error(data.error || '切换失败')
        }
      } catch (error) {
        ElMessage.error('切换失败: ' + (error.response?.data?.error || error.message))
      }
    },

    // 获取服务商名称
    getProviderName(provider) {
      const nameMap = {
        aliyun: '阿里云OSS',
        tencent: '腾讯云COS',
        qiniu: '七牛云Kodo',
        local: '本地存储'
      }
      return nameMap[provider] || provider
    },

    // 获取标签类型
    getProviderTagType(provider) {
      const typeMap = {
        aliyun: 'primary',
        tencent: 'success',
        qiniu: 'warning',
        local: 'info'
      }
      return typeMap[provider] || 'info'
    },

    async fetchAllApiConfigs() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/store/api/LaBasicSettingsController.php`, {
          action: 'get_openai',
          store_id: this.storeId,
          user_id: this.userId
        })

        if (data.data) {
          // 转换数据类型
          this.allApiConfigs = {
            ...data.data,
            openai_max_tokens: Number(data.data.openai_max_tokens) || 2000,
            openai_temperature: Number(data.data.openai_temperature) || 0.7,
            openai_enabled: Number(data.data.openai_enabled) || 1
          }
          // 更新当前选中模型的表单
          this.updateCurrentApiForm()
          // 更新所有模型状态
          this.updateAllModelStatus()
        }
      } catch (error) {
        console.error('获取接口配置失败:', error)
      }
    },

    async fetchAllStorageConfigs() {
      try {
        // 获取所有存储配置
        const configs = {}

        // 获取阿里云配置
        try {
          const aliyunRes = await axios.post(`${config.BASE_URL}/store/api/LaBasicSettingsController.php`, {
            action: 'get_oss',
            store_id: this.storeId,
            user_id: this.userId
          })
          if (aliyunRes.data.data) {
            configs.aliyun = aliyunRes.data.data
          }
        } catch (error) {
          console.error('获取阿里云配置失败:', error)
        }

        // 获取腾讯云配置
        try {
          const tencentRes = await axios.post(`${config.BASE_URL}/store/api/LaBasicSettingsController.php`, {
            action: 'get_tencent',
            store_id: this.storeId,
            user_id: this.userId
          })
          if (tencentRes.data.data) {
            configs.tencent = tencentRes.data.data
          }
        } catch (error) {
          console.error('获取腾讯云配置失败:', error)
        }

        // 获取七牛云配置
        try {
          const qiniuRes = await axios.post(`${config.BASE_URL}/store/api/LaBasicSettingsController.php`, {
            action: 'get_qiniu',
            store_id: this.storeId,
            user_id: this.userId
          })
          if (qiniuRes.data.data) {
            configs.qiniu = qiniuRes.data.data
          }
        } catch (error) {
          console.error('获取七牛云配置失败:', error)
        }

        this.allStorageConfigs = configs
        // 更新当前选中存储的表单
        this.updateCurrentStorageForm()
        // 更新所有存储状态
        this.updateAllStorageStatus()
      } catch (error) {
        console.error('获取存储配置失败:', error)
      }
    },

    updateCurrentApiForm() {
      const model = this.modelConfigs.find(m => m.id === this.activeModel)
      if (model) {
        const hasConfig = this.allApiConfigs.openai_url && this.allApiConfigs.openai_key
        const isCurrentModelConfigured = this.isModelConfigured(model)

        this.apiForm = {
          name: model.name,
          openai_url: hasConfig && isCurrentModelConfigured ? this.allApiConfigs.openai_url : model.preset.openai_url,
          openai_model: hasConfig && isCurrentModelConfigured ? this.allApiConfigs.openai_model : model.preset.openai_model,
          openai_key: hasConfig && isCurrentModelConfigured ? this.allApiConfigs.openai_key : '',
          openai_max_tokens: hasConfig && isCurrentModelConfigured ? this.allApiConfigs.openai_max_tokens : 2000,
          openai_temperature: hasConfig && isCurrentModelConfigured ? this.allApiConfigs.openai_temperature : 0.7,
          openai_enabled: hasConfig && isCurrentModelConfigured ? this.allApiConfigs.openai_enabled : 1
        }
      }
    },

    // 新增方法：检查模型是否配置
    isModelConfigured(model) {
      const currentUrl = this.allApiConfigs.openai_url
      if (!currentUrl || !this.allApiConfigs.openai_key) return false

      const modelUrl = model.preset.openai_url
      if (!modelUrl) return true // 自定义模型总是匹配

      // 提取基础URL进行比较
      const normalizeUrl = (url) => {
        return url.replace(/^https?:\/\//, '').split('/')[0]
      }

      return normalizeUrl(currentUrl) === normalizeUrl(modelUrl)
    },

    updateCurrentStorageForm() {
      const storage = this.storageConfigs.find(s => s.id === this.activeStorage)
      if (storage) {
        const config = this.allStorageConfigs[this.activeStorage] || {}

        let formData = {
          provider: storage.name,
          accessKey: '',
          secretKey: '',
          bucket: '',
          domain: '',
          region: this.getDefaultRegion(this.activeStorage),
          enabled: 1
        }

        if (this.activeStorage === 'aliyun') {
          formData = {
            ...formData,
            accessKey: config.aliyun_keyid || '',
            secretKey: config.aliyun_secret || '',
            bucket: config.aliyun_bucket || '',
            domain: config.aliyun_domain || '',
            region: config.endpoint || this.getDefaultRegion('aliyun'),
            enabled: config.aliyun_enabled !== undefined ? config.aliyun_enabled : 1
          }
        } else if (this.activeStorage === 'tencent') {
          formData = {
            ...formData,
            accessKey: config.tencent_secret_id || '',
            secretKey: config.tencent_secret_key || '',
            bucket: config.tencent_bucket || '',
            domain: config.tencent_domain || '',
            region: config.tencent_region || this.getDefaultRegion('tencent'),
            enabled: config.tencent_enabled !== undefined ? config.tencent_enabled : 1
          }
        } else if (this.activeStorage === 'qiniu') {
          formData = {
            ...formData,
            accessKey: config.qiniu_access_key || '',
            secretKey: config.qiniu_secret_key || '',
            bucket: config.qiniu_bucket || '',
            domain: config.qiniu_domain || '',
            region: config.qiniu_region || this.getDefaultRegion('qiniu'),
            enabled: config.qiniu_enabled !== undefined ? config.qiniu_enabled : 1
          }
        }

        this.storageForm = formData
      }
    },

    updateAllModelStatus() {
      // 根据配置数据更新所有模型状态
      const hasConfig = this.allApiConfigs.openai_url && this.allApiConfigs.openai_key

      this.modelConfigs.forEach(model => {
        if (hasConfig) {
          // 如果有配置，检查当前配置的URL是否匹配该模型的预设URL
          model.status = this.isModelConfigured(model)
        } else {
          model.status = false
        }
      })
    },

    updateAllStorageStatus() {
      // 根据配置数据更新所有存储状态
      this.storageConfigs.forEach(storage => {
        const config = this.allStorageConfigs[storage.id] || {}
        let hasConfig = false

        if (storage.id === 'aliyun') {
          hasConfig = !!(config.aliyun_keyid && config.aliyun_secret && config.aliyun_bucket)
        } else if (storage.id === 'tencent') {
          hasConfig = !!(config.tencent_secret_id && config.tencent_secret_key && config.tencent_bucket)
        } else if (storage.id === 'qiniu') {
          hasConfig = !!(config.qiniu_access_key && config.qiniu_secret_key && config.qiniu_bucket)
        }

        storage.status = hasConfig
      })
    },

    handleModelSelect(modelId) {
      this.activeModel = modelId
      this.updateCurrentApiForm()
    },

    handleStorageSelect(storageId) {
      this.activeStorage = storageId
      this.updateCurrentStorageForm()
    },

    getDefaultRegion(storageId) {
      const regionMap = {
        aliyun: 'oss-cn-beijing.aliyuncs.com',
        tencent: 'ap-nanjing',
        qiniu: 'z0'
      }
      return regionMap[storageId] || ''
    },

    setDefaultCozeUrl() {
      this.agentForm.apiUrl = 'https://api.coze.cn/v3/chat'
    },

    async fetchAgentConfig() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/store/api/LaBasicSettingsController.php`, {
          action: 'get_coze',
          store_id: this.storeId,
          user_id: this.userId
        })
        if (data.data) {
          this.agentForm = {
            name: data.data.coze_name || '',
            coze_access_token: data.data.coze_access_token || '',
            coze_botid: data.data.coze_botid || '',
            apiUrl: 'https://api.coze.cn/v3/chat',
            coze_chat_mode: data.data.coze_chat_mode || 'stream',
            coze_timeout: Number(data.data.coze_timeout) || 30,
            coze_enabled: Number(data.data.coze_enabled) || 1
          }
        }
      } catch (error) {
        console.error('获取智能体配置失败:', error)
      }
    },

    async handleApiSave() {
      if (!this.$refs.apiFormRef) return

      try {
        const valid = await this.$refs.apiFormRef.validate()
        if (!valid) return

        this.saving = true

        const { data } = await axios.post(
          `${config.BASE_URL}/store/api/LaBasicSettingsController.php`,
          {
            action: 'update_openai',
            store_id: this.storeId,
            user_id: this.userId,
            ...this.apiForm
          }
        )

        if (data.success) {
          ElMessage.success('接口配置保存成功')
          // 重新加载所有配置
          await this.fetchAllApiConfigs()
        } else {
          ElMessage.error(data.error || '保存失败')
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '服务器错误'
        ElMessage.error(`保存失败：${errorMsg}`)
        console.error('保存失败:', error)
      } finally {
        this.saving = false
      }
    },

    async handleApiTest() {
      if (!this.$refs.apiFormRef) return

      try {
        // 验证表单
        const valid = await this.$refs.apiFormRef.validate()
        if (!valid) return

        this.testingApiConnection = true
        this.apiTestResult = null
        this.apiTestMessage = '正在测试连接...'

        const response = await axios.post(
          this.apiForm.openai_url,
          {
            model: this.apiForm.openai_model,
            messages: [
              { "role": "system", "content": "You are a helpful assistant." },
              { "role": "user", "content": "Hello! Please reply with 'Connection successful'." }
            ],
            max_tokens: this.apiForm.openai_max_tokens,
            temperature: this.apiForm.openai_temperature,
            stream: false
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.apiForm.openai_key}`
            },
            timeout: 10000 // 10秒超时
          }
        )

        if (response.data && response.data.choices && response.data.choices.length > 0) {
          this.apiTestResult = true
          const reply = response.data.choices[0].message?.content || '空回复'
          this.apiTestMessage = `模型连接测试成功！收到回复: ${reply}`
          ElMessage.success('模型连接测试成功！')
        } else {
          throw new Error('接口返回格式不符合预期')
        }
      } catch (error) {
        console.error('模型连接测试失败:', error)

        this.apiTestResult = false
        let errorMessage = '模型连接测试失败'

        if (error.response) {
          // 服务器响应了但状态码不在 2xx 范围内
          const status = error.response.status
          errorMessage += ` (状态码: ${status})`

          if (error.response.data && error.response.data.error) {
            errorMessage += `: ${error.response.data.error.message || error.response.data.error}`
          } else if (error.response.data) {
            errorMessage += `: ${JSON.stringify(error.response.data)}`
          }

          // 常见状态码处理
          switch (status) {
            case 401:
              errorMessage = '认证失败，请检查API密钥是否正确'
              break
            case 403:
              errorMessage = '权限不足，请检查API密钥权限'
              break
            case 404:
              errorMessage = '接口地址不存在，请检查接口地址'
              break
            case 429:
              errorMessage = '请求频率超限，请稍后重试'
              break
            case 500:
              errorMessage = '服务器内部错误，请稍后重试'
              break
          }
        } else if (error.request) {
          // 请求已发出但没有收到响应
          if (error.code === 'ECONNABORTED') {
            errorMessage = '连接超时，请检查网络连接或接口地址'
          } else {
            errorMessage = '服务器无响应，请检查网络连接'
          }
        } else {
          // 请求设置出错
          errorMessage = `请求错误: ${error.message}`
        }

        this.apiTestMessage = errorMessage
        ElMessage.error(errorMessage)
      } finally {
        this.testingApiConnection = false
      }
    },

    async handleApiReset() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/store/api/LaBasicSettingsController.php`,
          {
            action: 'clear_openai',
            store_id: this.storeId,
            user_id: this.userId
          }
        )

        if (data.success) {
          ElMessage.success('接口配置已重置')
          // 重新加载所有配置
          await this.fetchAllApiConfigs()
          // 清除测试结果
          this.apiTestResult = null
          this.apiTestMessage = ''
        } else {
          ElMessage.error(data.error || '重置失败')
        }
      } catch (error) {
        ElMessage.error('重置失败: ' + (error.response?.data?.error || error.message))
      }
    },

    async handleAgentSave() {
      if (!this.$refs.agentFormRef) return

      try {
        const valid = await this.$refs.agentFormRef.validate()
        if (!valid) return

        this.saving = true

        const { data } = await axios.post(
          `${config.BASE_URL}/store/api/LaBasicSettingsController.php`,
          {
            action: 'update_coze',
            store_id: this.storeId,
            user_id: this.userId,
            coze_name: this.agentForm.name,
            coze_access_token: this.agentForm.coze_access_token,
            coze_botid: this.agentForm.coze_botid,
            coze_chat_mode: this.agentForm.coze_chat_mode,
            coze_timeout: this.agentForm.coze_timeout,
            coze_enabled: this.agentForm.coze_enabled
          }
        )

        if (data.success) {
          ElMessage.success('智能体配置保存成功')
        } else {
          ElMessage.error(data.error || '保存失败')
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '服务器错误'
        ElMessage.error(`保存失败：${errorMsg}`)
        console.error('保存失败:', error)
      } finally {
        this.saving = false
      }
    },

    async handleAgentTest() {
      if (!this.$refs.agentFormRef) return

      try {
        // 验证表单
        const valid = await this.$refs.agentFormRef.validate()
        if (!valid) return

        this.saving = true
        this.agentTestResult = null
        this.agentTestMessage = '正在测试连接...'

        // 构建测试数据
        const testData = {
          bot_id: this.agentForm.coze_botid,
          user_id: "test_user_" + Date.now(), // 使用唯一用户ID
          stream: this.agentForm.coze_chat_mode === 'stream',
          auto_save_history: true,
          additional_messages: [{
            role: "user",
            content: "你好，这是一条测试消息，请回复'连接成功'",
            content_type: "text"
          }]
        }

        // 如果是流式模式，需要特殊处理
        if (this.agentForm.coze_chat_mode === 'stream') {
          await this.testStreamConnection(testData)
        } else {
          // 非流式模式使用普通请求
          await this.testNormalConnection(testData)
        }

      } catch (error) {
        console.error('智能体测试失败:', error)

        this.agentTestResult = false
        let errorMessage = '智能体连接测试失败'

        if (error.response) {
          // 服务器返回错误状态码
          const status = error.response.status
          switch (status) {
            case 401:
              errorMessage = '认证失败，请检查智能体密钥是否正确'
              break
            case 403:
              errorMessage = '权限不足，请检查智能体ID是否正确'
              break
            case 404:
              errorMessage = '接口地址不存在，请检查接口地址是否正确'
              break
            case 500:
              errorMessage = '服务器内部错误，请稍后重试'
              break
            case 502:
              errorMessage = '网关错误，请检查网络连接'
              break
            default:
              errorMessage = `服务器错误 (${status}): ${error.response.data?.msg || error.message}`
          }
        } else if (error.request) {
          // 请求发送但无响应
          if (error.code === 'ECONNABORTED') {
            errorMessage = `连接超时 (${this.agentForm.coze_timeout}秒)，请检查网络或调整超时时间`
          } else {
            errorMessage = '网络连接失败，请检查网络设置和接口地址'
          }
        } else {
          // 其他错误
          errorMessage = error.message || '未知错误'
        }

        this.agentTestMessage = errorMessage
        ElMessage.error(errorMessage)
      } finally {
        this.saving = false
      }
    },

    // 新增方法：测试流式连接
    async testStreamConnection(testData) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        let receivedContent = ''
        let hasReceivedMessage = false

        xhr.open('POST', this.agentForm.apiUrl)
        xhr.setRequestHeader('Authorization', `Bearer ${this.agentForm.coze_access_token}`)
        xhr.setRequestHeader('Content-Type', 'application/json')
        xhr.setRequestHeader('Accept', 'text/event-stream')

        xhr.timeout = this.agentForm.coze_timeout * 1000

        xhr.onreadystatechange = () => {
          if (xhr.readyState === XMLHttpRequest.DONE) {
            if (xhr.status === 200) {
              // 检查是否收到了有效消息
              if (hasReceivedMessage) {
                this.agentTestResult = true
                this.agentTestMessage = `智能体连接测试成功！收到回复: ${receivedContent || '空回复'}`
                ElMessage.success('智能体连接测试成功！')
                resolve()
              } else {
                reject(new Error('未收到智能体回复'))
              }
            } else {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
            }
          }
        }

        xhr.onprogress = () => {
          // 处理流式数据
          const responseText = xhr.responseText
          const lines = responseText.split('\n')

          for (const line of lines) {
            if (line.startsWith('data:')) {
              const data = line.substring(5).trim()

              if (data === '[DONE]') {
                break
              }

              if (data) {
                try {
                  const eventData = JSON.parse(data)

                  // 检查消息内容
                  if (eventData.content && eventData.role === 'assistant' && eventData.type === 'answer') {
                    receivedContent += eventData.content
                    hasReceivedMessage = true
                    this.agentTestMessage = `正在接收回复: ${receivedContent}...`
                  }

                  // 检查对话完成事件
                  if (eventData.event === 'conversation.chat.completed' && eventData.status === 'completed') {
                    break
                  }
                } catch (e) {
                  // 忽略解析错误，继续处理其他行
                  console.log('解析事件数据失败:', e, '原始数据:', data)
                }
              }
            }
          }
        }

        xhr.ontimeout = () => {
          reject(new Error(`请求超时 (${this.agentForm.coze_timeout}秒)`))
        }

        xhr.onerror = () => {
          reject(new Error('网络请求失败'))
        }

        xhr.send(JSON.stringify(testData))
      })
    },

    // 新增方法：测试普通连接
    async testNormalConnection(testData) {
      const response = await axios.post(
        this.agentForm.apiUrl,
        testData,
        {
          headers: {
            'Authorization': `Bearer ${this.agentForm.coze_access_token}`,
            'Content-Type': 'application/json'
          },
          timeout: this.agentForm.coze_timeout * 1000
        }
      )

      // 检查响应格式
      if (response.data && response.data.messages) {
        // 标准响应格式
        const assistantMessage = response.data.messages.find(msg =>
          msg.role === 'assistant' && msg.type === 'answer'
        )

        if (assistantMessage && assistantMessage.content) {
          this.agentTestResult = true
          this.agentTestMessage = `智能体连接测试成功！收到回复: ${assistantMessage.content}`
          ElMessage.success('智能体连接测试成功！')
        } else {
          throw new Error('未收到有效的智能体回复')
        }
      } else if (response.data.code === 0) {
        // 简单的成功状态码
        this.agentTestResult = true
        this.agentTestMessage = '智能体连接测试成功！'
        ElMessage.success('智能体连接测试成功！')
      } else {
        throw new Error(response.data.msg || '智能体返回错误状态')
      }
    },

    async handleAgentReset() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/store/api/LaBasicSettingsController.php`,
          {
            action: 'clear_coze',
            store_id: this.storeId,
            user_id: this.userId
          }
        )

        if (data.success) {
          ElMessage.success('智能体配置已重置')
          this.agentForm = {
            name: '',
            coze_access_token: '',
            coze_botid: '',
            apiUrl: 'https://api.coze.cn/v3/chat',
            coze_chat_mode: 'stream',
            coze_timeout: 30,
            coze_enabled: 1
          }
          // 清除测试结果
          this.agentTestResult = null
          this.agentTestMessage = ''
        } else {
          ElMessage.error(data.error || '重置失败')
        }
      } catch (error) {
        ElMessage.error('重置失败: ' + (error.response?.data?.error || error.message))
      }
    },

    async handleStorageSave() {
      if (!this.$refs.storageFormRef) return

      try {
        const valid = await this.$refs.storageFormRef.validate()
        if (!valid) return

        this.saving = true

        const actionMap = {
          aliyun: 'update_oss',
          tencent: 'update_tencent',
          qiniu: 'update_qiniu'
        }

        const formData = {
          store_id: this.storeId,
          user_id: this.userId
        }

        // 根据存储类型构建不同的数据格式
        if (this.activeStorage === 'aliyun') {
          Object.assign(formData, {
            aliyun_keyid: this.storageForm.accessKey,
            aliyun_secret: this.storageForm.secretKey,
            aliyun_bucket: this.storageForm.bucket,
            aliyun_domain: this.storageForm.domain,
            endpoint: this.storageForm.region,
            aliyun_enabled: this.storageForm.enabled
          })
        } else if (this.activeStorage === 'tencent') {
          Object.assign(formData, {
            tencent_secret_id: this.storageForm.accessKey,
            tencent_secret_key: this.storageForm.secretKey,
            tencent_bucket: this.storageForm.bucket,
            tencent_domain: this.storageForm.domain,
            tencent_region: this.storageForm.region,
            tencent_enabled: this.storageForm.enabled
          })
        } else if (this.activeStorage === 'qiniu') {
          Object.assign(formData, {
            qiniu_access_key: this.storageForm.accessKey,
            qiniu_secret_key: this.storageForm.secretKey,
            qiniu_bucket: this.storageForm.bucket,
            qiniu_domain: this.storageForm.domain,
            qiniu_region: this.storageForm.region,
            qiniu_enabled: this.storageForm.enabled
          })
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/store/api/LaBasicSettingsController.php`,
          {
            action: actionMap[this.activeStorage],
            ...formData
          }
        )

        if (data.success) {
          ElMessage.success('存储配置保存成功')
          // 更新云服务商为当前保存的存储类型
          await this.updateCloudProvider(this.activeStorage)
          // 重新加载所有存储配置
          await this.fetchAllStorageConfigs()
        } else {
          ElMessage.error(data.error || '保存失败')
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '服务器错误'
        ElMessage.error(`保存失败：${errorMsg}`)
        console.error('保存失败:', error)
      } finally {
        this.saving = false
      }
    },

    async handleStorageTest() {
      if (!this.$refs.storageFormRef) return

      try {
        const valid = await this.$refs.storageFormRef.validate()
        if (!valid) return

        this.testingStorage = true
        this.storageTestResult = null
        this.storageTestMessage = ''

        // 构建测试数据
        const testData = {
          cloud_type: this.activeStorage,
          store_id: this.storeId,
          user_id: this.userId
        }

        // 根据存储类型添加不同的字段
        if (this.activeStorage === 'aliyun') {
          Object.assign(testData, {
            aliyun_keyid: this.storageForm.accessKey,
            aliyun_secret: this.storageForm.secretKey,
            endpoint: this.storageForm.region,
            aliyun_bucket: this.storageForm.bucket
          })
        } else if (this.activeStorage === 'tencent') {
          Object.assign(testData, {
            tencent_secret_id: this.storageForm.accessKey,
            tencent_secret_key: this.storageForm.secretKey,
            tencent_bucket: this.storageForm.bucket,
            tencent_region: this.storageForm.region
          })
        } else if (this.activeStorage === 'qiniu') {
          Object.assign(testData, {
            qiniu_access_key: this.storageForm.accessKey,
            qiniu_secret_key: this.storageForm.secretKey,
            qiniu_bucket: this.storageForm.bucket,
            qiniu_region: this.storageForm.region
          })
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/store/api/test_oss_connection.php`,
          testData
        )

        this.storageTestResult = data.success
        this.storageTestMessage = data.message || (data.success ? '连接测试成功' : '连接测试失败')

        if (data.success) {
          ElMessage.success(`${this.getProviderName(this.activeStorage)}连接测试成功`)
        } else {
          ElMessage.error(`${this.getProviderName(this.activeStorage)}连接测试失败: ${data.error || '未知错误'}`)
        }
      } catch (error) {
        this.storageTestResult = false
        this.storageTestMessage = error.response?.data?.error || error.message || '测试连接时发生错误'
        ElMessage.error(`测试连接失败: ${this.storageTestMessage}`)
      } finally {
        this.testingStorage = false
      }
    },

    async handleStorageReset() {
      try {
        const actionMap = {
          aliyun: 'clear_oss',
          tencent: 'clear_tencent',
          qiniu: 'clear_qiniu'
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/store/api/LaBasicSettingsController.php`,
          {
            action: actionMap[this.activeStorage],
            store_id: this.storeId,
            user_id: this.userId
          }
        )

        if (data.success) {
          ElMessage.success('存储配置已重置')
          // 重新加载所有存储配置
          await this.fetchAllStorageConfigs()
          // 重置测试结果
          this.storageTestResult = null
          this.storageTestMessage = ''
        } else {
          ElMessage.error(data.error || '重置失败')
        }
      } catch (error) {
        ElMessage.error('重置失败: ' + (error.response?.data?.error || error.message))
      }
    }
  },
  mounted() {
    // 初始化加载所有配置
    this.fetchAllApiConfigs()
    this.fetchAgentConfig()
    this.fetchAllStorageConfigs()
    this.fetchCloudProvider()
  }
}
</script>

<style scoped lang="scss">
.ai-agent-container {
  padding: 24px;
  min-height: calc(100vh - 160px);
  background: transparent;
}

.main-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
}

.button-group {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.main-button {
  padding: 12px 24px;
  background: hsla(0, 0%, 100%, .1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #e0e6ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ff7048;
    border-color: #ff7048;
    transform: translateY(-2px);
  }

  &.active {
    background: #ff7048;
    border-color: #ff7048;
  }
}

.content-area {
  min-height: 500px;
}

.config-content {
  .config-header {
    margin-bottom: 24px;
    padding: 20px;
    background: rgba(255, 112, 72, 0.1);
    border-radius: 8px;
    border: 1px solid rgba(255, 112, 72, 0.2);

    .config-title {
      color: #e0e6ff;
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 8px;
    }

    .config-description {
      color: #a0a8b8;
      font-size: 14px;
      margin: 0;
    }

    .current-provider-info {
      margin-top: 12px;

      .el-tag {
        font-size: 14px;
        padding: 8px 12px;

        .el-icon {
          margin-right: 6px;
        }
      }
    }
  }
}

.model-cards,
.storage-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.model-card,
.storage-card {
  background: hsla(0, 0%, 100%, .1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    border-color: #ff7048;
    transform: translateY(-2px);
  }

  &.active {
    border-color: #ff7048;
    background: rgba(255, 112, 72, 0.1);
  }

  .model-icon,
  .storage-icon {
    color: #ff7048;
    flex-shrink: 0;
  }

  .model-info,
  .storage-info {
    flex: 1;

    .model-name,
    .storage-name {
      color: #e0e6ff;
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 4px;
    }

    .model-desc,
    .storage-desc {
      color: #a0a8b8;
      font-size: 12px;
      margin: 0;
      line-height: 1.4;
    }
  }

  .model-status,
  .storage-status {
    flex-shrink: 0;
  }
}

.config-form,
.agent-form {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 8px;
  padding: 24px;

  :deep(.el-form-item__label) {
    color: #e0e6ff !important;
  }

  :deep(.el-input__inner),
  :deep(.el-textarea__inner) {
    color: #e0e6ff !important;
  }

  .test-result {
    margin-top: 20px;

    :deep(.el-alert) {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);

      .el-alert__title {
        color: #e0e6ff;
      }

      .el-alert__description {
        color: #a0a8b8;
      }
    }
  }
}

.unit-text {
  margin-left: 8px;
  color: #e0e6ff;
}

.slider-value {
  margin-left: 12px;
  color: #ff7048;
  font-weight: 600;
  min-width: 30px;
  display: inline-block;
}

@media (max-width: 768px) {
  .ai-agent-container {
    padding: 16px;
  }

  .button-group {
    gap: 12px;

    .main-button {
      padding: 10px 20px;
      font-size: 13px;
    }
  }

  .model-cards,
  .storage-cards {
    grid-template-columns: 1fr;
  }

  .config-form,
  .agent-form {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: 16px;
  }

  .button-group {
    justify-content: center;
  }
}
</style>

<style>
.config-form .el-form-item__label,
.agent-form .el-form-item__label {
  color: #e0e6ff !important;
}

.config-form .el-input__inner,
.config-form .el-textarea__inner,
.agent-form .el-input__inner,
.agent-form .el-textarea__inner {
  color: #ffffff !important;
}

.config-form .el-input__inner::placeholder,
.config-form .el-textarea__inner::placeholder,
.agent-form .el-input__inner::placeholder,
.agent-form .el-textarea__inner::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

.config-form .el-slider__bar {
  background: #ff7048 !important;
}

.config-form .el-slider__button {
  border-color: #ff7048 !important;
}

.config-form .el-switch__core,
.agent-form .el-switch__core {
  background: rgba(255, 255, 255, 0.2) !important;
  border: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.config-form .el-switch.is-checked .el-switch__core,
.agent-form .el-switch.is-checked .el-switch__core {
  background: #ff7048 !important;
  border-color: #ff7048 !important;
}

.config-form .el-button,
.agent-form .el-button {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.config-form .el-button--primary,
.agent-form .el-button--primary {
  background: #ff7048 !important;
  border-color: #ff7048 !important;
}

.config-form .el-button--primary:hover,
.agent-form .el-button--primary:hover {
  background: #e55a3a !important;
  border-color: #e55a3a !important;
  transform: translateY(-1px);
}
</style>